import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Plus, Pencil, Check, X, Loader2, Trash2, LogOut, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddProfileModal = ({ onClose, onCreated }) => {
  const { createProfile } = useAuth();
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [avatars, setAvatars] = useState([]);
  const [isKid, setIsKid] = useState(false);
  const [kidAge, setKidAge] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/profiles/avatars`).then((r) => {
      setAvatars(r.data.avatars || []);
      setAvatar((r.data.avatars || [])[0]);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError('Please enter a name.');
    if (isKid) {
      const age = parseInt(kidAge, 10);
      if (!age || age < 1 || age > 17) return setError('Kid age must be between 1 and 17.');
    }
    setSaving(true);
    try {
      const p = await createProfile({
        name: name.trim(),
        avatar,
        is_kid: isKid,
        kid_age: isKid ? parseInt(kidAge, 10) : null,
      });
      onCreated(p);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#1a1a1a] rounded-lg w-full max-w-lg p-6 md:p-8 text-white border border-white/10"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-2xl font-bold">Add a profile</h2>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-4 mb-5">
          <img src={avatar} alt="avatar" className="w-20 h-20 rounded-md bg-white/5 border border-white/10" />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={24}
            placeholder="Profile name"
            className="flex-1 bg-[#333] rounded px-3 py-2.5 text-white placeholder-white/40 outline-none focus:ring-2 focus:ring-white/40"
            autoFocus
          />
        </div>

        <p className="text-xs text-white/60 mb-2">Choose an avatar</p>
        <div className="grid grid-cols-8 gap-2 mb-5">
          {avatars.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAvatar(a)}
              className={`rounded-md overflow-hidden border-2 transition-transform hover:scale-105 ${
                avatar === a ? 'border-white' : 'border-transparent'
              }`}
            >
              <img src={a} alt="avatar" className="w-full aspect-square bg-white/5" />
            </button>
          ))}
        </div>

        <label className="flex items-center gap-3 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isKid}
            onChange={(e) => setIsKid(e.target.checked)}
            className="w-4 h-4 accent-[#e11d48]"
          />
          <span className="text-sm">Kid profile (filters mature content)</span>
        </label>

        {isKid && (
          <div className="mb-4">
            <label className="block text-xs text-white/60 mb-1">Kid&apos;s age</label>
            <input
              type="number"
              min={1}
              max={17}
              value={kidAge}
              onChange={(e) => setKidAge(e.target.value)}
              placeholder="e.g. 9"
              className="w-32 bg-[#333] rounded px-3 py-2 text-white outline-none focus:ring-2 focus:ring-white/40"
            />
            <p className="text-[11px] text-white/50 mt-1">Under 14 also disables rentals for safety.</p>
          </div>
        )}

        {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-white/20 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 rounded bg-[#e11d48] hover:bg-[#c9163f] disabled:opacity-60 font-semibold flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Create
          </button>
        </div>
      </form>
    </div>
  );
};

const ProfilePicker = () => {
  const { user, profiles, selectProfile, deleteProfile, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [managing, setManaging] = useState(false);
  const [adding, setAdding] = useState(false);

  const pick = (p) => {
    if (managing) return;
    selectProfile(p);
    navigate('/');
  };

  const canAdd = profiles.length < 5;

  return (
    <div className="min-h-screen bg-[#141414] text-white flex flex-col">
      <div className="flex items-center justify-between px-6 md:px-14 py-6">
        <span
          className="text-3xl md:text-4xl font-black tracking-tight"
          style={{
            fontFamily: "'Bebas Neue', 'Oswald', Impact, sans-serif",
            color: '#e11d48', letterSpacing: '0.03em',
            textShadow: '0 2px 14px rgba(225,29,72,0.4)',
          }}
        >
          NOVA<span style={{ color: '#ffffff' }}>REEL</span><span style={{ color: '#e11d48' }}>TV</span>
        </span>
        <div className="flex items-center gap-4 text-sm">
          <span className="hidden md:inline text-white/60">{user?.phone}</span>
          {isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              data-testid="profile-picker-admin-btn"
              className="flex items-center gap-1 text-amber-300 hover:text-amber-200 transition-colors"
            >
              <Settings className="w-4 h-4" /> Admin
            </button>
          )}
          <button onClick={logout} className="flex items-center gap-1 text-white/70 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <h1 className="text-3xl md:text-5xl font-medium mb-10 text-center">
          {managing ? 'Manage Profiles' : "Who's watching?"}
        </h1>

        <div className="flex flex-wrap justify-center gap-4 md:gap-8 max-w-4xl">
          {profiles.map((p) => (
            <div
              key={p.id}
              onClick={() => {
                if (managing) return;
                pick(p);
              }}
              role="button"
              tabIndex={0}
              className={`flex flex-col items-center gap-3 group select-none ${managing ? '' : 'cursor-pointer'}`}
            >
              <div
                className={`relative w-24 h-24 md:w-32 md:h-32 rounded-md overflow-hidden border-2 transition-all ${
                  managing ? 'border-white/30' : 'border-transparent group-hover:border-white group-hover:scale-105'
                }`}
              >
                <img src={p.avatar} alt={p.name} className="w-full h-full bg-white/5 pointer-events-none" />
                {managing && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center pointer-events-none">
                    <Pencil className="w-8 h-8 text-white" />
                  </div>
                )}
                {p.is_kid && (
                  <span className="absolute top-1 left-1 bg-white text-black text-[10px] font-bold px-1.5 py-0.5 rounded uppercase pointer-events-none">
                    Kid
                  </span>
                )}
              </div>
              <span className="text-sm md:text-base text-white/70 group-hover:text-white">{p.name}</span>
              {managing && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Delete profile "${p.name}"?`)) deleteProfile(p.id);
                  }}
                  className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
            </div>
          ))}

          {canAdd && (
            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                data-testid="add-profile-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setAdding(true);
                }}
                className="w-24 h-24 md:w-32 md:h-32 rounded-md bg-white/5 border-2 border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white transition-colors"
              >
                <Plus className="w-10 h-10 pointer-events-none" />
              </button>
              <span className="text-sm md:text-base text-white/60">Add Profile</span>
            </div>
          )}
        </div>

        <button
          onClick={() => setManaging((v) => !v)}
          className="mt-14 px-6 py-2 border border-white/40 text-white/80 uppercase tracking-widest text-sm hover:text-white hover:border-white transition-colors"
        >
          {managing ? 'Done' : 'Manage Profiles'}
        </button>
      </div>

      {adding && (
        <AddProfileModal
          key={`add-modal-${profiles.length}`}
          onClose={() => setAdding(false)}
          onCreated={() => setAdding(false)}
        />
      )}
    </div>
  );
};

export default ProfilePicker;
